import { useRouter } from 'next/router';
import React from 'react';
import styles from './blog-card.module.css';
import { Img, ResourcesRes } from '../../typescript/pages';
import { translate } from '../../helper/translate';
import { brandDefaultResourceImages } from '../../typescript/utils';
import Link from 'next/link';
import aa from '../../typescript/algoliaInsights';
import moment from 'moment';
import { getCachedAsset } from '../../helper/utils';

// Use environment variable for asset prefix if needed, otherwise empty string
const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX || '';

const BlogCard = React.memo(function BlogCard({
  blog_post,
  component_type,
  size,
  number,
}: {
  blog_post: ResourcesRes;
  component_type: string;
  size?: string;
  number?: number;
}) {
  const { locale } = useRouter();
  const activeLang = locale ?? 'en-us';
  const isDefaultLocale = locale === 'en-us';

  const getBlogCardClass = (component_type: string) => {
    switch (component_type) {
      case 'Resource Grid':
        return styles.resourceCarousel;
      case 'Resource List':
        return styles.resourceList;
      case 'Blog Grid':
        return styles.blogGrid;
      default:
        return styles.resourceCarousel;
    }
  };

  const splitDescription = (description: string) => {
    if (!description || description.length === 0) return '';

    const words = description.split(' ');
    const truncated = words
      .slice(0, 12)
      .join(' ')
      ?.replace(/<[^>]+>/g, '');

    return words.length > 10 ? `${truncated}...` : truncated;
  };

  const getTitle = (blog_post_item: ResourcesRes) => {
    switch (component_type) {
      case 'Resource Grid':
        if (
          blog_post_item._content_type_uid === 'page' &&
          blog_post_item.page_components?.[0]?.hero_banner?.banner_title
        ) {
          return blog_post_item.page_components[0].hero_banner.banner_title;
        }
        if (blog_post_item._content_type_uid === 'metal_selector_overview') {
          return blog_post_item.header_title;
        }
        return blog_post_item.title;
      case 'Resource List':
      case 'Blog Grid':
        if (
          blog_post_item._content_type_uid === 'page' &&
          blog_post_item.page_components?.[0]?.hero_banner?.banner_title
        ) {
          return blog_post_item.page_components[0].hero_banner.banner_title;
        }
        if (blog_post_item._content_type_uid === 'metal_selector_overview') {
          return blog_post_item.header_title;
        }
        switch (
          blog_post_item._content_type_uid ||
          (blog_post_item.type && blog_post_item.type?.toLowerCase()) ||
          ''
        ) {
          case 'webinar':
            return translate('webinar', activeLang);
          case 'whitepaper':
            return translate('whitepaper', activeLang);
          case 'article':
            return translate('article', activeLang);
          case 'case_study':
          case 'case study':
            return translate('case_study', activeLang);
          case 'video':
            return translate('video', activeLang);
          case 'news':
            return translate('news', activeLang);
          case 'event':
            return translate('event', activeLang);
          default:
            return blog_post_item.title; // Fallback to the post's title
        }
      default:
        return blog_post_item.title;
    }
  };

  const formatDate = (date: string, locale?: string) => {
    if (locale) {
      moment.locale(locale);
    }

    return moment(date).format('MMMM D, YYYY');
  }

  const PlayBtnSVG = () => {
    return (
      //TODO: Lets move this out eventually.
      <svg
        width="104"
        height="104"
        viewBox="0 0 104 104"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M52.0002 4.68018C25.8941 4.68018 4.68018 25.8941 4.68018 52.0002C4.68018 78.1063 25.8941 99.3202 52.0002 99.3202C78.1063 99.3202 99.3202 78.1063 99.3202 52.0002C99.3202 25.8941 78.1063 4.68018 52.0002 4.68018ZM70.5132 53.7671L43.2663 69.4732C42.9535 69.6804 42.5391 69.786 42.2263 69.786C41.9135 69.786 41.4991 69.6804 41.1863 69.4732C40.5606 69.0589 40.1463 68.4332 40.1463 67.7061V36.2949C40.1463 35.5677 40.5606 34.8406 41.1863 34.5278C41.8119 34.1134 42.6406 34.1134 43.2663 34.5278L70.5132 50.2339C71.1389 50.6482 71.5532 51.2738 71.5532 52.001C71.5532 52.7282 71.1348 53.4543 70.5132 53.7671Z"
          fill="white"
        />
      </svg>
    );
  };

  const getDescription = (blog_post_item: ResourcesRes) => {
    switch (component_type) {
      case 'Resource Grid':
        if (blog_post_item._content_type_uid === 'page' && blog_post_item.description) {
          return blog_post_item.description;
        }
        if (
          blog_post_item._content_type_uid === 'metal_selector_overview' &&
          blog_post_item.header_description
        ) {
          return blog_post_item.header_description;
        }

        if (
          blog_post_item._content_type_uid === 'metal_selector_overview' &&
          blog_post_item.description
        ) {
          return blog_post_item.description;
        }
        return splitDescription(blog_post_item.summary);
      case 'Blog Grid':
      case 'Resource List':
        if (blog_post_item._content_type_uid === 'page' && blog_post_item.description) {
          return blog_post_item.description;
        }
        if (
          blog_post_item._content_type_uid === 'metal_selector_overview' &&
          blog_post_item.header_description
        ) {
          return blog_post_item.header_description;
        }

        if (
          blog_post_item._content_type_uid === 'metal_selector_overview' &&
          blog_post_item.description
        ) {
          return blog_post_item.description;
        }
        return blog_post_item.title;
      default:
        return blog_post_item.title;
    }
  };

  const getYouTubeThumbnail = (url: string): Img | null => {
    const regex = /(?:youtube\.com\/(?:.*[?&]v=|v\/|embed\/|shorts\/)|youtu\.be\/)([^"&?/ ]{11})/;
    const match = url.match(regex);

    return match
      ? {
          url: `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`,
          description: 'YouTube Thumbnail',
          uid: match[1],
          title: 'YouTube Video',
          filename: '',
          content_type: 'Img',
        }
      : null;
  };

  // Asset UID of the Contentstack default placeholder set on the featured_image field.
  // When no image is chosen by an editor, Contentstack fills this in automatically.
  const CONTENTSTACK_DEFAULT_IMAGE_UID = 'bltbe37e282ea60ffda';

  const isPlaceholderImage = (img: Img | null | undefined): boolean => {
    if (!img?.url) return true;
    return img.url.includes(CONTENTSTACK_DEFAULT_IMAGE_UID);
  };

  const getFeaturedImage = (isVideo: boolean) => {
    if (isVideo && blog_post.youtube) {
      const youtubeThumb = getYouTubeThumbnail(blog_post.youtube);
      if (youtubeThumb) {
        return youtubeThumb;
      }
    }

    // Check if featured_image exists, has a valid URL, and is not the Contentstack default placeholder
    if (
      blog_post.featured_image?.url &&
      blog_post.featured_image.url.trim().length > 0 &&
      !isPlaceholderImage(blog_post.featured_image)
    ) {
      return blog_post.featured_image;
    }

    if (
      blog_post._content_type_uid === 'page' &&
      blog_post.page_components &&
      blog_post.page_components.length > 0
    ) {
      const heroMedia = blog_post.page_components[0]?.hero_banner?.banner_media;
      if (heroMedia?.url && heroMedia.url.trim().length > 0) {
        return heroMedia;
      }
    }

    // Return null if no valid image found, so fallback can be used
    return null;
  };

  const isVideo =
    blog_post.type?.toLowerCase() === 'video' || blog_post._content_type_uid === 'video';
  // Determine the featured image
  const featuredImage = getFeaturedImage(isVideo);

  const isAbsoluteUrl = blog_post.url?.startsWith('http');
  // Check if URL already contains a locale prefix (e.g., /de-de/, /en-us/, etc.)
  const localePattern = /^\/([a-z]{2}-[a-z]{2})\//;
  const urlAlreadyHasLocale = blog_post.url?.match(localePattern);
  
  const safeHref = isAbsoluteUrl
    ? blog_post.url
    : urlAlreadyHasLocale
      ? blog_post.url ?? ''
      : `${isDefaultLocale ? '' : `/${locale}`}${blog_post.url ?? ''}`;
  // Clean URL - remove existing query params and let Next.js Image handle optimization
  const getCleanImageUrl = (url: string): string => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
    } catch {
      // If URL parsing fails, try to remove query string manually
      return url.split('?')[0];
    }
  };

  // Check if featuredImage exists and has a valid URL
  const hasValidImage = featuredImage?.url && featuredImage.url.trim().length > 0;
  const brand = process.env.NEXT_PUBLIC_CONTENTSTACK_BRAND  || 'form_technologies';
  const defaultFallbackImage = brandDefaultResourceImages[brand] ?? brandDefaultResourceImages['form_technologies'];
  const resolvedImageSrc = hasValidImage
    ? getCleanImageUrl(getCachedAsset(featuredImage.url, {width: 1200, quality: 75}))
    : defaultFallbackImage;

  const renderCardImage = () => {
    const targetWidth = size === 'wide' ? 328 : 268;
    const retinaWidth = 384;
    const widths = Array.from(new Set([targetWidth, retinaWidth])).sort((a, b) => a - b);
    const buildOptimizedSrc = (width: number) =>
      `${assetPrefix}/_next/image?url=${encodeURIComponent(resolvedImageSrc)}&w=${width}&q=75`;

    const srcSet = widths.map(width => `${buildOptimizedSrc(width)} ${width}w`).join(', ');

    return (
      <img
        key={resolvedImageSrc}
        className={styles.imageIcon}
        src={buildOptimizedSrc(targetWidth)}
        srcSet={srcSet}
        sizes={`${targetWidth}px`}
        width={targetWidth}
        height={185}
        decoding="async"
        loading="lazy"
        alt={blog_post.featured_image_alt || featuredImage?.description || 'Blog Image'}
        onLoad={event => {
          event.currentTarget.style.backgroundImage = 'none';
        }}
      />
    );
  };

  return (
    <>
      <Link
        href={safeHref}
        className={styles.linkWrapper}
        onClick={() => {
          if (blog_post.__queryID && blog_post.__position !== undefined) {
            aa('clickedObjectIDsAfterSearch', {
              eventName: 'Resource Clicked',
              index: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME!,
              objectIDs: [blog_post.uid],
              positions: [blog_post.__position],
              queryID: blog_post.__queryID,
            });
          } else {
            // fallback tracking if it's not from Algolia search
            aa('clickedObjectIDs', {
              eventName: 'Resource Clicked (No Search)',
              index: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME!,
              objectIDs: [blog_post.uid],
            });
          }
        }}
      >
        <div
          className={`${styles.blogCard} ${getBlogCardClass(component_type)} ${size === 'wide' ? styles.wide : ''}`}
        >
          <div className={`${styles.imageContainer}`}>
            {renderCardImage()}

            {isVideo && (
              <div className={styles.playButtonWrapper}>
                <PlayBtnSVG />
              </div>
            )}

            {featuredImage && component_type === 'Resource Grid' && (
              <div className={styles.imageOverlay}></div>
            )}

            {number && <div className={styles.numberOverlay}>{number}</div>}
          </div>

          <div className={styles.content}>
            <div className={styles.copy}>
              <div className={styles.copyTop}>
                {blog_post.metadata_tags?.date && blog_post.metadata_tags?.show_date === true && (
                  <span className={styles.date}>{formatDate(blog_post.metadata_tags.date, blog_post.locale)}</span>
                  
                )}
                <div>
                  <h3 className={`${styles.title} line-clamp-2`}>{getTitle(blog_post)}</h3>
                </div>
              </div>
              <p className={`${styles.description} line-clamp-4`}>{getDescription(blog_post)}</p>
              {blog_post.metadata_tags?.author && blog_post.metadata_tags?.show_author === true && (
                <p className={styles.author}><span><strong>Author:</strong> {blog_post.metadata_tags.author}</span></p>
              )}
            </div>
          </div>
        </div>
      </Link>
    </>
  );
});

export default BlogCard;
